/**
 * RoadmapCommentsContent
 *
 * Main UI for the roadmap ("Ideas") comments sheet: header with the live count,
 * a scrollable comment list (infinite scroll + empty/loading states), and the
 * input bar. Structural sibling of ContentCommentsContent, swapping the media
 * comment hook/row for their roadmap equivalents.
 *
 * UI choice (deviation from Flutter): Flutter's title renders the count phrase
 * ("No comments" / "1 comment" / "N comments", roadmap_comments_bottom_sheet
 * .dart:791-817). The web reuses the content port's `l10n.comments.title (N)`
 * to stay consistent with the existing comments sheet; behaviour is equivalent.
 */

import React, { useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { ExpauseLoadingIndicator } from '@/presentation/components/common/loadingIndicators/ExpauseLoadingIndicator';
import {
  VirtualList,
  type VirtualListHandle,
} from '@/presentation/components/common/virtualList/VirtualList';
import { qaAttr } from '@/presentation/utils/qaAttr';
import { useRoadmapComments } from './hooks/useRoadmapComments';
import { RoadmapCommentRow } from './components/RoadmapCommentRow';
import { RoadmapCommentInput } from './components/RoadmapCommentInput';
import type { RoadmapItemEntity } from '@/domain/entities/roadmap/roadmapItemEntity';
import type { RoadmapCommentInfoEntity } from '@/domain/entities/info/roadmapCommentInfoEntity';
import type { RoadmapCommentEntity } from '@/domain/entities/roadmapComment/roadmapCommentEntity';

export interface RoadmapCommentsContentProps {
  item: RoadmapItemEntity;
  commentInfo?: RoadmapCommentInfoEntity;
  screenKey?: string;
}

export const RoadmapCommentsContent: React.FC<RoadmapCommentsContentProps> = ({
  item,
  commentInfo,
  screenKey,
}) => {
  const { theme } = useTheme();
  const { l10n } = useLocale();
  const responsive = useResponsiveDesign();

  const {
    comments,
    isLoading,
    commentCount,
    commentToReply,
    highlightedCommentId,
    loadMore,
    expandReplies,
    collapseReplies,
    addComment,
    likeComment,
    setCommentToReply,
    cancelReply,
  } = useRoadmapComments({ item, commentInfo });

  const listRef = useRef<VirtualListHandle>(null);

  // Scroll the highlighted (deep-linked) comment into view. With VirtualList
  // the target row may be off-screen / unmounted, so we drive the scroll
  // through the imperative scrollToIndex API rather than a DOM ref.
  useEffect(() => {
    if (!highlightedCommentId) return;
    const index = comments.findIndex(c => c.id === highlightedCommentId);
    if (index >= 0) {
      listRef.current?.scrollToIndex(index, 'smooth');
    }
  }, [highlightedCommentId, comments]);

  const handleSend = async (text: string): Promise<boolean> => {
    const ok = await addComment(text);
    if (!ok) toast.error(l10n.comments.errorSending);
    return ok;
  };

  const handleLike = async (comment: RoadmapCommentEntity, liked: boolean) => {
    try {
      await likeComment(comment, liked);
    } catch {
      toast.error(l10n.comments.errorLiking);
    }
  };

  // Coarse lifecycle token reusing existing state: spinner shown with no rows
  // yet is `loading`; settled with no rows is `empty`; otherwise `ready`.
  const sheetStatus =
    isLoading && comments.length === 0
      ? 'loading'
      : comments.length === 0
        ? 'empty'
        : 'ready';

  return (
    <div
      {...qaAttr({ id: 'roadmap-comments-sheet', status: sheetStatus })}
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <div
        {...qaAttr({ id: 'roadmap-comments-sheet-title', value: commentCount })}
        style={{
          padding: responsive.spacing.lg,
          borderBottom: `${responsive.border.thin}px solid ${theme.border}`,
          textAlign: 'center',
          fontWeight: responsive.fontWeight.semibold,
          color: theme.text.primary,
          fontSize: responsive.font.base,
        }}
      >
        {l10n.comments.title} ({commentCount})
      </div>

      <div
        className="scrollbar-hide"
        style={{
          flex: 1,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {isLoading && comments.length === 0 ? (
          <div style={{ padding: responsive.spacing.xl, display: 'flex', justifyContent: 'center' }}>
            <ExpauseLoadingIndicator />
          </div>
        ) : comments.length === 0 ? (
          <div
            style={{
              padding: responsive.spacing['3xl'],
              textAlign: 'center',
              color: theme.text.secondary,
              fontSize: responsive.font.sm,
            }}
          >
            {l10n.comments.noComments}
          </div>
        ) : (
          <>
            <VirtualList<RoadmapCommentEntity>
              ref={listRef}
              qa={{ id: 'roadmap-comments-list' }}
              items={comments}
              getItemKey={c => c.id}
              renderItem={c => (
                <RoadmapCommentRow
                  comment={c}
                  highlightedCommentId={highlightedCommentId}
                  onReply={setCommentToReply}
                  onExpandReplies={expandReplies}
                  onCollapseReplies={collapseReplies}
                  onLike={handleLike}
                />
              )}
              gap={0}
              onReachedEnd={loadMore}
              reachedEndThreshold={200}
            />

            {isLoading && comments.length > 0 && (
              <div style={{ padding: responsive.spacing.base, display: 'flex', justifyContent: 'center' }}>
                <ExpauseLoadingIndicator />
              </div>
            )}
          </>
        )}
      </div>

      <RoadmapCommentInput
        replyTo={commentToReply}
        onCancelReply={cancelReply}
        onSend={handleSend}
        screenKey={screenKey}
      />
    </div>
  );
};
